import * as React from "react";

import { AIAgentThreadContext } from "@/AgentThread/AgentThreadSidebar/AIAgentThreadContext";
import { ContextType } from "@/AgentThread/AgentThreadSidebar/contextType";

/**
 * Provides a minimal, read-only AIAgentThreadContext for rendering AgentThreadByIdView
 * outside of the normal thread sidebar (e.g. in admin views).
 *
 * Most mutation handlers are no-ops and interactive features are disabled.
 * ``handleHistoryThreadClicked`` is the one exception: ``ForkAgentThreadButton``
 * calls it on a successful fork to navigate the viewer to the new thread,
 * so the parent can pass ``onAgentThreadClicked`` to receive that signal.
 */
interface ReadOnlyAIAgentThreadProviderProps {
  children: React.ReactNode;
  /** Called when a child requests navigation to a different thread (today,
   *  only ``ForkAgentThreadButton`` after a successful fork). When omitted the
   *  request is dropped silently. */
  onAgentThreadClicked?: (threadId: string) => void;
  /** Forwarded into context so message rows can render an admin-only debug
   *  control under each message. */
  renderMessageDebugControl?: (messageSequenceNum: number) => React.ReactNode;
}

export const ReadOnlyAIAgentThreadProvider: React.FC<
  ReadOnlyAIAgentThreadProviderProps
> = ({ children, onAgentThreadClicked, renderMessageDebugControl }) => {
  const noop = React.useCallback(() => {}, []);

  const handleHistoryThreadClicked = React.useCallback(
    (threadId: string) => {
      onAgentThreadClicked?.(threadId);
    },
    [onAgentThreadClicked],
  );

  const value = React.useMemo(
    () => ({
      isFullscreen: false,
      showHistory: false,
      autoEnabled: false,
      currentThreadId: undefined,
      context: { dataset_ids: [], file_ids: [] },
      contextDisplayNames: { datasets: {}, files: {} },
      modelProfile: undefined,

      setIsFullscreen: noop as (value: boolean) => void,
      setShowHistory: noop as (value: boolean) => void,
      setAutoEnabled: noop as (value: boolean) => void,
      setCurrentThreadId: noop as (threadId: string | undefined) => void,
      setModelProfile: noop as (profile: string) => void,

      handleContextDelete: noop as (
        contextType: ContextType,
        contextContent: string,
      ) => void,
      handleNewThread: noop,
      handleToggleFullscreen: noop,
      handleToggleHistory: noop,
      handleToggleAuto: noop,
      handleHistoryThreadClicked,
      handleLinkClick: noop,
      handleResetUIState: noop,

      renderMessageDebugControl,
    }),
    [noop, handleHistoryThreadClicked, renderMessageDebugControl],
  );

  return (
    <AIAgentThreadContext.Provider value={value}>
      {children}
    </AIAgentThreadContext.Provider>
  );
};
